import { FC, useMemo, useState } from 'react'
import styles from 'components/features/coaching/advisorMeeting/AvailabilityPicker/AvailabilityPicker.styl'
import { AdvisorUid } from 'components/features/coaching/advisor/types/advisor'
import { createTimeslotSelector } from 'components/features/coaching/advisorMeeting/selectors/advisorsSelector'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import moment from 'moment'
import Button from 'components/features/pfm/Button/Button'
import cn from 'classnames'
import { TimeSlot } from 'components/features/coaching/advisorMeeting/types/requests'
import { LoadingOverlay } from 'components/atoms/preloaders/LoadingOverlay'

type AvailabilityPickerProps = {
  uid: AdvisorUid
  onSelect: (date: string) => void
}
export const AvailabilityPicker: FC<AvailabilityPickerProps> = (props) => {
  const { onSelect, uid } = props

  const selectTimeslots = useMemo(createTimeslotSelector, [])
  const timeslots = useStoreSelector((state) => selectTimeslots(state, props))
  const isLoading = useStoreSelector((state) => state.data.advisors.timeslotsRequest[uid]?.isLoading ?? false)

  const [daysShown, setDaysShown] = useState(4)

  const timeslotGroups = useMemo(() => {
    if (!timeslots) {
      return null
    }

    const groups: Record<string, TimeSlot[]> = timeslots.reduce((timeslotGroup, timeslot) => {
      const date = moment(timeslot.date).format('YYYY-MM-DD')
      if (!timeslotGroup[date]) {
        timeslotGroup[date] = []
      }
      timeslotGroup[date].push(timeslot)
      return timeslotGroup
    }, {} as Record<string, TimeSlot[]>)

    return Object.keys(groups).map((date) => {
      return {
        date,
        slots: groups[date],
      }
    })
  }, [timeslots])

  const datesChild = useMemo(() => {
    if (!timeslotGroups) {
      return null
    }
    return timeslotGroups.slice(0, daysShown).map((group) => {
      const date = moment(group.date)

      const renderSlot = (slot: TimeSlot) => (
        <li key={ slot.date } className={ cn(styles.button, !slot.isAvailable && styles.disabled) }>
          <Button
            fullWidth
            size='tiny'
            disabled={ !slot.isAvailable }
            onClick={ () => onSelect(slot.date) }
          >
            { moment(slot.date).format('h:mm a') }
          </Button>
        </li>
      )

      return (
        <li key={ group.date } >
          <div className={ styles.title }>
            <div className={ styles.date }>
              { date.format('MMMM Do') }
            </div>
            <div className={ styles.weekday }>
              { date.format('dddd') }
            </div>
          </div>
          <ul className={ styles.timeslots }>
            { group.slots.map(renderSlot) }
          </ul>
        </li>
      )
    })
  }, [timeslotGroups, daysShown, onSelect])

  const showMoreButton = useMemo(() => {
    if (!timeslotGroups || daysShown >= timeslotGroups.length) {
      return null
    }
    return (
      <div className={ styles.moreTimeslotsLink }>
        <Button text='Show more days' theme='lightBlue' size='small' fullWidth onClick={ () => setDaysShown(daysShown + 5) } />
      </div>
    )
  }, [daysShown, timeslotGroups])

  if (!datesChild) {
    return null
  }

  return (
    <>
      <div className={ styles.availabilityPicker }>
        { isLoading && (
          <LoadingOverlay />
        )}
        <ul className={ styles.datesList }>
          { datesChild }
        </ul>
        { showMoreButton }
      </div>
    </>
  )
}
